


import React, { useState, useEffect } from 'react';

import logoAndes from '../assets/logoAndesBlanco.png';
import TicketDetailPanel from './TicketDetailPanel';
import TicketStatusBadge from './TicketStatusBadge';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

const API_BASE_URL = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');

const TicketManagementSection = ({ onNewTicket = () => {}, onSelectTicket = () => {} }) => {
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [responsableFilter, setResponsableFilter] = useState('');
  const [solicitanteFilter, setSolicitanteFilter] = useState('');
  const [vencidoFilter, setVencidoFilter] = useState(''); // '', 'vencido', 'noVencido'

  useEffect(() => {
    const fetchTickets = async () => {
      setLoading(true);
      try {
        const resp = await fetch(`${API_BASE_URL}/webhook/listar_casos_abiertos_pendientes`);
        const json = await resp.json();
        if (!json.error && Array.isArray(json.data)) {
          setData(json.data);
        } else {
          setError(json.msg || 'Error al cargar los tickets');
        }
      } catch (err) {
        setError('Error al conectar con el servidor');
      }
      setLoading(false);
    };
    fetchTickets();
  }, []);

  // Función para limpiar y capitalizar responsables
  const limpiarResponsable = (r) => {
    if (!r) return '';
    let nombre = r.split(' - ')[0].trim();
    // Capitalizar cada palabra
    return nombre.replace(/\b\w+/g, w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
  };

  // Para el filtro: solo nombres capitalizados únicos
  const responsablesFiltro = Array.from(new Set(data.flatMap(grupo => grupo.casos.map(c => limpiarResponsable(c.responsable))).filter(Boolean)));
  // Para el panel de detalle: array de objetos {id, nombre}
  const responsablesPanelRaw = data.flatMap(grupo => grupo.casos.map(c => {
    if (typeof c.responsable === 'string') {
      const [nombre, id] = c.responsable.split(' - ');
      return { id: id ? id.trim() : '', nombre: nombre ? nombre.trim() : '' };
    }
    return null;
  })).filter(r => r && r.id && r.nombre);
  const responsablesPanel = Array.from(new Map(responsablesPanelRaw.map(r => [r.id, r])).values());
  const solicitantes = Array.from(new Set(data.map(grupo => grupo.nombre_solicitante).filter(Boolean)));

  // Filtrar los datos
  const nowDate = new Date();
  const isVencido = (fechaVencimiento) => {
    if (!fechaVencimiento) {
      return false;
    }
    // Formato: "2025-07-28 08:35:01"
    // Convertir a formato ISO para Date
    const isoString = fechaVencimiento.replace(' ', 'T');
    const fecha = new Date(isoString);
    const vencido = fecha < nowDate;
    return vencido;
  };

  const filteredData = data
    .filter(grupo => !solicitanteFilter || grupo.nombre_solicitante === solicitanteFilter)
    .map(grupo => ({
      ...grupo,
      casos: grupo.casos.filter(caso => {
        const responsableOk = !responsableFilter || limpiarResponsable(caso.responsable) === responsableFilter;
        const vencidoOk = vencidoFilter === ''
          ? true
          : vencidoFilter === 'vencido'
            ? isVencido(caso.fecha_vencimiento)
            : !isVencido(caso.fecha_vencimiento);
        return responsableOk && vencidoOk;
      })
    }))
    .filter(grupo => grupo.casos.length > 0);

  // Calcular totales por mes y datos para gráfica
  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();
  let totalAbiertos = 0;
  let totalPendientes = 0;
  let totalVencidos = 0;
  const casosPorMes = {};
  data.forEach(grupo => {
    grupo.casos.forEach(caso => {
      if (caso.fecha_creacion) {
        const fecha = new Date(caso.fecha_creacion.replace(' ', 'T'));
        const key = `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}`;
        casosPorMes[key] = (casosPorMes[key] || 0) + 1;
        if (fecha.getMonth() + 1 === currentMonth && fecha.getFullYear() === currentYear) {
          if (caso.status === 'Abierto') totalAbiertos++;
          if (caso.status === 'Pendiente') totalPendientes++;
          const vencido = isVencido(caso.fecha_vencimiento);
          if (vencido) {
            totalVencidos++;
          }
        }
      }
    });
  });

  // Preparar datos para la gráfica
  const sortedMeses = Object.keys(casosPorMes).sort();
  const chartData = {
    labels: sortedMeses,
    datasets: [
      {
        label: 'Casos por mes',
        data: sortedMeses.map(mes => casosPorMes[mes]),
        fill: true,
        borderColor: '#38b2ac',
        backgroundColor: 'rgba(56,178,172,0.1)',
        pointBackgroundColor: '#38b2ac',
        pointBorderColor: '#38b2ac',
        tension: 0.4,
      }
    ]
  };
  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { display: false },
      title: { display: true, text: 'Casos por mes' },
      tooltip: {
        enabled: true,
        callbacks: {
          label: function(context) {
            return `Cantidad: ${context.parsed.y}`;
          }
        }
      },
      datalabels: {
        display: true,
        color: '#222',
        font: { weight: 'bold' },
        align: 'top',
        formatter: function(value) { return value; }
      }
    },
    scales: {
      x: { title: { display: true, text: 'Mes' } },
      y: { title: { display: true, text: 'Cantidad' }, beginAtZero: true, precision: 0 }
    },
    elements: {
      line: { borderWidth: 3 },
      point: { radius: 6, hoverRadius: 8 }
    }
  };

  const handleSelectTicket = (ticket) => {
    setSelectedTicket(ticket);
    onSelectTicket(ticket);
  };

  const handleCloseDetail = () => {
    setSelectedTicket(null);
  };

  return (
    <div className="p-8 bg-gray-50 min-h-screen">
      <h1 className="text-4xl font-extrabold text-gray-900 mb-8">Gestión de Tickets</h1>

      {/* Panel superior: Filtros | Cards | Gráfica */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 items-stretch">
        {/* Filtros */}
        <div className="bg-white rounded-xl shadow p-6 flex flex-col justify-center border border-gray-200">
          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-700 mb-1">Filtrar por responsable</label>
            <select
              className="px-4 py-2 rounded-lg border border-gray-300 bg-white shadow-sm w-full"
              value={responsableFilter}
              onChange={e => setResponsableFilter(e.target.value)}
            >
              <option value="">Todos</option>
              {responsablesFiltro.map((r, idx) => (
                <option key={idx} value={r}>{r}</option>
              ))}
            </select>
          </div>
          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-700 mb-1">Filtrar por solicitante</label>
            <select
              className="px-4 py-2 rounded-lg border border-gray-300 bg-white shadow-sm w-full"
              value={solicitanteFilter}
              onChange={e => setSolicitanteFilter(e.target.value)}
            >
              <option value="">Todos</option>
              {solicitantes.map((s, idx) => (
                <option key={idx} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Filtrar por vencimiento</label>
            <select
              className="px-4 py-2 rounded-lg border border-gray-300 bg-white shadow-sm w-full"
              value={vencidoFilter}
              onChange={e => setVencidoFilter(e.target.value)}
            >
              <option value="">Todos</option>
              <option value="vencido">Vencidos</option>
              <option value="noVencido">No vencidos</option>
            </select>
          </div>
        </div>
        {/* Cards */}
        <div className="flex flex-col gap-6 justify-center">
          <div className="bg-custom-green-100 border-l-8 border-custom-green-500 rounded-xl shadow p-3">
            <div className="text-base font-bold text-custom-green-700 mb-1">Abiertos este mes</div>
            <div className="text-2xl font-extrabold text-custom-green-700">{totalAbiertos}</div>
          </div>
          <div className="bg-yellow-100 border-l-8 border-yellow-500 rounded-xl shadow p-3">
            <div className="text-base font-bold text-yellow-700 mb-1">Pendientes este mes</div>
            <div className="text-2xl font-extrabold text-yellow-700">{totalPendientes}</div>
          </div>
          <div className="bg-red-100 border-l-8 border-red-500 rounded-xl shadow p-3">
            <div className="text-base font-bold text-red-700 mb-1">Vencidos este mes</div>
            <div className="text-2xl font-extrabold text-red-700">{totalVencidos}</div>
          </div>
        </div>
        {/* Gráfica */}
        <div className="bg-white rounded-xl shadow p-6 border border-gray-200 flex items-center justify-center">
          <Line data={chartData} options={chartOptions} />
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
        <div className="p-6 border-b border-gray-100 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-800">Tickets por Solicitante</h2>
          <button
            onClick={onNewTicket}
            className="px-5 py-2 bg-custom-blue text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 inline-block mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Nuevo Ticket
          </button>
        </div>
        <div className="overflow-x-auto">
          {loading ? (
            <div className="p-6 text-center text-gray-500">Cargando tickets...</div>
          ) : error ? (
            <div className="p-6 text-center text-red-500">{error}</div>
          ) : (
            filteredData.length === 0 ? (
              <div className="p-6 text-center text-gray-500">No hay tickets abiertos o pendientes.</div>
            ) : (
              filteredData.map((grupo, idx) => (
                <div key={idx} className="mb-8">
                  <h3 className="text-lg font-semibold text-custom-green-700 mb-2">{grupo.nombre_solicitante}</h3>
                  <table className="min-w-full bg-white border border-gray-200 rounded-lg mb-2">
                    <thead>
                      <tr>
                        {/* <th className="px-4 py-2 border-b text-left">ID Caso</th> */}
                        <th className="px-4 py-2 border-b text-left">Petición</th>
                        <th className="px-4 py-2 border-b text-left">Estado</th>
                        <th className="px-4 py-2 border-b text-left">Responsable</th>
                        <th className="px-4 py-2 border-b text-left">Fecha</th>
                        <th className="px-4 py-2 border-b text-left">Puesto</th>
                        <th className="px-4 py-2 border-b text-left">Vencimiento</th>
                      </tr>
                    </thead>
                    <tbody>
                      {grupo.casos.map((caso, i) => {
                        const vencido = isVencido(caso.fecha_vencimiento);
                        return (
                          <tr key={i} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleSelectTicket(caso)}>
                            {/* <td className="px-4 py-2 border-b">{caso.id}</td> */}
                            <td className="px-4 py-2 border-b">{caso.numero_peticion}</td>
                            <td className="px-4 py-2 border-b">
                              <span className="flex items-center gap-2">
                                <TicketStatusBadge status={caso.status} />
                                <span className="mx-1">-</span>
                                {vencido ? (
                                  <span className="px-2 py-1 rounded text-xs font-bold bg-red-100 text-red-700 border border-red-300">Vencido</span>
                                ) : (
                                  <span className="px-2 py-1 rounded text-xs font-bold bg-green-100 text-green-700 border border-green-300">No vencido</span>
                                )}
                              </span>
                            </td>
                            <td className="px-4 py-2 border-b">{limpiarResponsable(caso.responsable)}</td>
                            <td className="px-4 py-2 border-b">{caso.fecha_creacion}</td>
                            <td className="px-4 py-2 border-b">{caso.numero_puesto}</td>
                            <td className="px-4 py-2 border-b">{caso.fecha_vencimiento}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ))
            )
          )}
        </div>
      </div>
      {selectedTicket && (
        <TicketDetailPanel ticket={selectedTicket} onClose={handleCloseDetail} responsables={responsablesPanel} />
      )}
    </div>
  );
};

export default TicketManagementSection;